import React from "react";
import { Pressable, Text, View } from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import styles from "../NotificationsCommsScreen.style";
import {
  NotificationStatusTab,
  PortalNotification,
} from "../hooks/useNotificationsComms";
import NotificationItem from "./NotificationItem";

type Props = {
  notifications: PortalNotification[];
  activeTab: NotificationStatusTab;
  tabCounts: Record<NotificationStatusTab, number>;
  tabs: { key: NotificationStatusTab; label: string }[];
  loading?: boolean;
  onTabChange: (tab: NotificationStatusTab) => void;
  onToggleRead: (id: string) => void;
  onDelete?: (id: string) => void;
  onPressLinkedTicket?: (ticketId: string) => void;
  onEdit?: (id: string) => void;
  isDark?: boolean;
  colors?: any;
};

// ── Skeleton loader ───────────────────────────────────────────────────────────

const SkeletonRow: React.FC<{ isDark?: boolean }> = ({ isDark }) => (
  <View style={[styles.skeletonRow, isDark && { backgroundColor: "#202020", borderLeftColor: "#363636" }]}>
    <View style={[styles.skeletonLine, { width: "70%" }, isDark && { backgroundColor: "#2B2B2B" }]} />
    <View style={[styles.skeletonLineShort, isDark && { backgroundColor: "#2B2B2B" }]} />
    <View style={[styles.skeletonLine, { width: "100%", marginTop: 4 }, isDark && { backgroundColor: "#2B2B2B" }]} />
    <View style={[styles.skeletonLine, { width: "85%" }, isDark && { backgroundColor: "#2B2B2B" }]} />
  </View>
);

// ── Empty state ───────────────────────────────────────────────────────────────

const EmptyState: React.FC<{ tab: NotificationStatusTab; isDark?: boolean }> = ({ tab, isDark }) => {
  const messages: Record<NotificationStatusTab, { title: string; desc: string }> = {
    active: {
      title: "No active notifications",
      desc: "There are no active notifications right now. Create one to keep the field informed.",
    },
    scheduled: {
      title: "No scheduled notifications",
      desc: "Notifications you schedule for a future date will appear here.",
    },
    sent: {
      title: "No sent notifications",
      desc: "Notifications that have been sent to recipients will appear here.",
    },
    draft: {
      title: "No draft notifications",
      desc: "Notification drafts you've saved without sending will appear here.",
    },
  };
  const copy = messages[tab];
  return (
    <View style={styles.emptyState}>
      <View style={[styles.emptyIconWrap, isDark && { backgroundColor: "#0d2a44" }]}>
        <MaterialIcons name="notifications-none" size={26} color={isDark ? "#3D8BFF" : "#005CBC"} />
      </View>
      <Text style={[styles.emptyTitle, isDark && { color: "#F2F2F2" }]}>{copy.title}</Text>
      <Text style={[styles.emptyDesc, isDark && { color: "#B0B0B0" }]}>{copy.desc}</Text>
    </View>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const NotificationList: React.FC<Props> = ({
  notifications,
  activeTab,
  tabCounts,
  tabs,
  loading = false,
  onTabChange,
  onToggleRead,
  onDelete,
  onPressLinkedTicket,
  onEdit,
  isDark,
  colors,
}) => {
  return (
    <View>
      {/* ── Tab bar ─────────────────────────────────────────────────────── */}
      <View style={[styles.tabBar, isDark && { borderBottomColor: "#404040" }]}>
        {tabs.map((tab) => {
          const isActive = tab.key === activeTab;
          const count = tabCounts[tab.key] ?? 0;
          return (
            <Pressable
              key={tab.key}
              style={[styles.tab, isActive ? styles.tabActive : null, isActive && isDark && { borderBottomColor: "#3D8BFF" }]}
              onPress={() => onTabChange(tab.key)}
            >
              <Text style={[styles.tabText, isDark && { color: "#B0B0B0" }, isActive ? styles.tabTextActive : null, isActive && isDark && { color: "#3D8BFF" }]}>
                {tab.label}
              </Text>
              {count > 0 ? (
                <View style={[styles.tabCount, isActive ? styles.tabCountActive : null, isDark && !isActive && { backgroundColor: "#2B2B2B" }, isDark && isActive && { backgroundColor: "#0d2a44" }]}>
                  <Text
                    style={[styles.tabCountText, isDark && !isActive && { color: "#B0B0B0" }, isActive ? styles.tabCountTextActive : null, isActive && isDark && { color: "#3D8BFF" }]}
                  >
                    {count}
                  </Text>
                </View>
              ) : null}
            </Pressable>
          );
        })}
      </View>

      {/* ── Content ──────────────────────────────────────────────────────── */}
      {loading ? (
        <>
          <SkeletonRow isDark={isDark} />
          <SkeletonRow isDark={isDark} />
          <SkeletonRow isDark={isDark} />
        </>
      ) : notifications.length === 0 ? (
        <EmptyState tab={activeTab} isDark={isDark} />
      ) : (
        notifications.map((notif) => (
          <NotificationItem
            key={notif.id}
            notification={notif}
            onToggleRead={onToggleRead}
            onDelete={onDelete}
            onPressLinkedTicket={onPressLinkedTicket}
            onEdit={onEdit}
            isDark={isDark}
            colors={colors}
          />
        ))
      )}
    </View>
  );
};

export default NotificationList;
